import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Wrench } from 'lucide-react';
import { api } from '../../services/api';

function formatDate(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw || '-';
  const d = raw.slice(0, 10);
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
}

// Bandeja del Coordinador Área Transportes (quien autoriza salidas de
// taller desde oct-2026). El backend ya filtra por rol, sede y vigencia
// (7 días): otros usuarios reciben la lista vacía y no ven el bloque.
// embedded: se muestra dentro de una pestaña de Portería; onInfo avisa si
// el usuario puede autorizar (para mostrar la pestaña) y cuántas hay.
export default function SeguridadPendingExits({ location, embedded = false, onInfo }) {
  const [canAuthorize, setCanAuthorize] = useState(false);
  const [items, setItems] = useState([]);
  const [expiredCount, setExpiredCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [signingId, setSigningId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.maintenanceServiceOrders.pendingSeguridad({ sede: location || '' });
      setItems(res?.ok && Array.isArray(res.data) ? res.data : []);
      setExpiredCount(Number(res?.expired_count || 0));
      setCanAuthorize(!!res?.can_authorize);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => { load(); }, [load]);

  const authorize = async (order) => {
    if (!window.confirm(`¿Autorizar la salida de taller del vehículo ${order.plate} (${order.order_number})?`)) return;
    setSigningId(order.id);
    try {
      const res = await api.maintenanceServiceOrders.authorizeExit({ id: order.id });
      if (!res?.ok) throw new Error(res?.error || 'No fue posible autorizar la salida.');
      setItems((prev) => prev.filter((it) => it.id !== order.id));
    } catch (e) {
      alert(String(e?.message || 'No fue posible autorizar la salida.').replace(/^HTTP \d+:\s*/, ''));
    } finally {
      setSigningId(null);
    }
  };

  useEffect(() => { if (onInfo) onInfo({ visible: canAuthorize, count: items.length }); }, [onInfo, canAuthorize, items.length]);

  if (embedded && !loading && items.length === 0) {
    return <div className="p-4 text-center text-sm text-gray-500">No hay salidas de taller por autorizar en esta sede.</div>;
  }
  if (loading || items.length === 0) return null;

  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="mb-1 flex items-center gap-2">
        <Wrench size={18} className="text-amber-700" />
        <h3 className="text-sm font-semibold text-amber-900">Salidas de taller por autorizar ({items.length})</h3>
      </div>
      <p className="mb-3 text-xs text-amber-700">
        Órdenes de servicio (FT-TRP-130) de esta sede que necesitan tu autorización como Coordinador Área Transportes para que portería deje salir el vehículo.
        {expiredCount > 0 && ` Hay ${expiredCount} con más de 7 días sin autorizar: aparecen en Portería › Vencidos.`}
      </p>
      <div className="space-y-2">
        {items.map((order) => (
          <div key={order.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-white p-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">{order.plate} · {order.order_number}</div>
              <div className="text-xs text-gray-500">
                {order.workshop_name || 'Sin taller'} · Salida: {formatDate(order.exit_date)} {order.exit_time || ''}
                {order.programada && <span className="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">Programada</span>}
              </div>
              {Number(order.total_cost) > 0 && (
                <div className="text-xs text-gray-500">Costo total: ${Number(order.total_cost).toLocaleString('es-CO')}</div>
              )}
            </div>
            <button
              type="button"
              disabled={signingId === order.id || order.programada}
              title={order.programada ? 'Se autoriza el día de la salida' : undefined}
              onClick={() => authorize(order)}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: '#16A34A' }}
            >
              <ShieldCheck size={14} /> {signingId === order.id ? 'Autorizando...' : 'Autorizar salida'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
